import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../Leads.css";
import { FOUND_LEADS as COLUMN_MAP } from "../../../utils/column_maps";
import LoadingSpinner from "../../../components/LoadingSpinner";
import axiosInstance from "../../../api/axiosInstance";

const FIELD_MAP = {
  zipcode: "zipcode",
  zip_code: "zipcode",
  beds: "bedrooms",
  baths: "bathrooms",
};

const FoundLeads = () => {
  // const location = useLocation();
  const [allLeads, setAllLeads] = useState([]);
  const [leads, setLeads] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const leadsPerPage = 25;
  const [totalLeads, setTotalLeads] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPageFromParams = Number(searchParams.get("page")) || 1;
  const [currentPage, setCurrentPage] = useState(currentPageFromParams);

  const indexOfLastLead = currentPage * leadsPerPage;
  const indexOfFirstLead = indexOfLastLead - leadsPerPage;

  const totalPages = Math.ceil(totalLeads / leadsPerPage);

  const fetchAllLeads = async (showLoader = true) => {
    try {
      if (showLoader) setIsLoading(true);

      const sources = [
        { url: `api/ispeed_properties/leads-s?found=true`, provider: "S" },
        { url: `api/ispeed_properties/leads-m?found=true`, provider: "M" },
        { url: `api/ispeed_properties/leads-b?found=true`, provider: "B" },
      ];

      // Use axiosInstance instead of fetch
      const responses = await Promise.all(sources.map(({ url }) => axiosInstance.get(url)));

      const leads = responses.flatMap((res, i) => res.data.data.map((row) => ({ ...row, provider: sources[i].provider })));

      const cleaned = leads.map((row) => {
        const obj = { provider: row.provider };

        COLUMN_MAP.forEach(({ key }) => {
          const matchingApiKey = Object.entries(FIELD_MAP).find(([apiKey, normalized]) => normalized === key && row[apiKey] !== undefined);

          let val = "";
          if (row[key] !== undefined) {
            val = row[key];
          } else if (matchingApiKey) {
            val = row[matchingApiKey[0]];
          }

          if (key === "lot_size_sqft") {
            const acres = parseFloat(row["lot_size_acres"]);
            if (acres) {
              val = Math.floor(acres * 43560);
            } else if (row["lot_land_sqft"]) {
              val = row["lot_land_sqft"];
            } else {
              val = "";
            }
          }

          if (["bedrooms", "bathrooms"].includes(key) && typeof val === "string") {
            val = parseInt(val) || "";
          }

          obj[key] = val;
        });

        return obj;
      });

      cleaned.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setAllLeads(cleaned);
      setTotalLeads(cleaned.length);
    } catch (err) {
      console.error("Error fetching leads:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Update page when URL params change
  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    setCurrentPage(page);
  }, [searchParams]);

  // Fetch all leads on initial load
  useEffect(() => {
    fetchAllLeads();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    fetchAllLeads(true);

    const interval = setInterval(() => {
      fetchAllLeads(false);
    }, 10000);

    return () => clearInterval(interval); // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (allLeads.length === 0) return;

    let filteredLeads = allLeads;

    if (search) {
      const s = search.toLowerCase().trim();

      // Check if "column: value" pattern
      if (s.includes(":")) {
        const [label, value] = s.split(":").map((v) => v.trim());

        // find column key by matching label
        const col = COLUMN_MAP.find((c) => c.label.toLowerCase() === label.toLowerCase());

        if (col && value) {
          filteredLeads = allLeads.filter((lead) => {
            const cell = lead[col.key];
            return cell && cell.toString().toLowerCase().includes(value);
          });
        }
      } else {
        // normal search in all columns
        const searchLower = s;
        filteredLeads = allLeads.filter((lead) => Object.values(lead).some((value) => value && value.toString().toLowerCase().includes(searchLower)));
      }
    }

    setTotalLeads(filteredLeads.length);

    const currentLeads = filteredLeads.slice(indexOfFirstLead, indexOfLastLead);
    setLeads(currentLeads);
  }, [allLeads, currentPage, search, indexOfFirstLead, indexOfLastLead]);

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;

    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set("page", page);
    setSearchParams(newParams);
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (isNaN(date.getTime())) return iso;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getAlignClass = (align) => {
    switch (align) {
      case "center":
        return "!text-center";
      case "right":
        return "!text-right";
      default:
        return "!text-left";
    }
  };

  const totalTableWidth = COLUMN_MAP.reduce((sum, col) => sum + col.width, 0);

  // Generate pagination buttons
  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    // Always show first page
    buttons.push(
      <button key={1} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === 1 ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(1)}>
        1
      </button>,
    );

    // Calculate start and end of visible page range
    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(2, endPage - maxVisiblePages + 1);
    }

    // Add ellipsis after first page if needed
    if (startPage > 2) {
      buttons.push(
        <span key="ellipsis-start" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button key={i} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-pointer ${currentPage === i ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(i)}>
          {i}
        </button>,
      );
    }

    // Add ellipsis before last page if needed
    if (endPage < totalPages - 1) {
      buttons.push(
        <span key="ellipsis-end" className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white cursor-default bg-[#008080]">
          ...
        </span>,
      );
    }

    // Add last page if there is more than one page
    if (totalPages > 1) {
      buttons.push(
        <button key={totalPages} className={`flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-none cursor-pointer ${currentPage === totalPages ? "bg-[#006666]" : "bg-[#008080] hover:bg-[#009999]"}`} onClick={() => changePage(totalPages)}>
          {totalPages}
        </button>,
      );
    }

    return buttons;
  };

  return (
    <>
      <div className="input_fields_container">
        <div className="">
          <input type="text" placeholder="Search leads..." value={search} onChange={(e) => setSearch(e.target.value)} className="!mb-4 !p-2 !border !border-gray-300 !rounded-lg !w-full focus:!outline-none focus:!ring-2 focus:!ring-blue-500" />
        </div>
      </div>

      <div className="">
        <div className="!overflow-auto !w-full">
          {isLoading ? (
            <div className="!p-4 !text-left !text-gray-500 w-full">
              <div className="w-full max-w-screen text-center">
                <LoadingSpinner size={30} textSize={20} />
              </div>
            </div>
          ) : (
            <>
              <table className="!border !border-gray-300 !bg-white !rounded-lg !shadow !table-auto" style={{ width: `${totalTableWidth}px` }}>
                <thead className="!bg-gray-100 sticky top-0 z-[1]">
                  <tr>
                    {COLUMN_MAP.map((col) => (
                      <th
                        key={col.key}
                        className={`!border !border-gray-300 !px-1 !py-2 !font-semibold text-nowrap ${getAlignClass(col.headerAlign)}`}
                        style={{
                          minWidth: `${col.width}px`,
                          width: `${col.width}px`,
                        }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.length > 0 ? (
                    leads.map((row, idx) => (
                      <tr key={idx} className="hover:!bg-gray-50 even:!bg-gray-50">
                        {COLUMN_MAP.map((col) => (
                          <td
                            key={col.key}
                            className={`!border !border-gray-300 !px-1 !py-2 !truncate text-nowrap ${getAlignClass(col.cellAlign)}`}
                            style={{
                              maxWidth: `${col.width}px`,
                              width: `${col.width}px`,
                            }}
                            title={row[col.key]}
                          >
                            {col.key === "created_at" ? formatTime(row[col.key]) : row[col.key]}
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={COLUMN_MAP.length} className="!border !border-gray-300 !p-4 !text-center !text-gray-500">
                        {search ? "No matching leads found." : "No leads found."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
        {totalLeads > 0 && (
          <div className="flex flex-col items-center !mb-10 mt-4">
            <span className="text-sm text-gray-700">
              Showing <span className="font-semibold text-gray-900">{indexOfFirstLead + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(indexOfLastLead, totalLeads)}</span> of <span className="font-semibold text-gray-900">{totalLeads}</span> Entries
            </span>

            {/* Pagination */}
            <div className="inline-flex !mt-2 xs:mt-0">
              {/* Prev Button */}
              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-s cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage - 1)} disabled={currentPage === 1}>
                Prev
              </button>

              {getPaginationButtons()}

              {/* Next Button */}
              <button className="flex items-center justify-center !px-4 h-10 text-base font-medium text-white rounded-e cursor-pointer bg-[#008080] hover:bg-[#009999] disabled:bg-[#006666]" onClick={() => changePage(currentPage + 1)} disabled={currentPage === totalPages}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default FoundLeads;
